import type { AdminBooking } from '@mds/shared/types/booking';

import Button from '@/components/ui/button';
import Chip from '@/components/ui/chip';
import {
  bookingStatusLabel,
  bookingStatusTone,
  bookingSpace,
  bookingWhen,
} from '@/domain/bookings';
import { accessLabel, durationLabel } from '@/domain/packages';
import { stamp } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';

/** One labelled line in the details list. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-mono text-sm tracking-wide text-ink-muted uppercase">{label}</dt>
      <dd className="m-0">{children}</dd>
    </>
  );
}

/**
 * Everything the API holds about one booking — who holds the desk or room and
 * how to reach them, what it came to, every payment taken against it, and what
 * can still be done to it.
 *
 * Payment history is append-only on the server, so it is listed rather than
 * summarised away: a cancelled booking keeps its rows and its retained deposit.
 */
export default function BookingDetails({
  booking,
  onCollect,
  onChangePackage,
  onCancel,
  busy = false,
}: {
  booking: AdminBooking;
  /** Omitted when the account cannot record payments, or nothing is owed. */
  onCollect?: () => void;
  /** Omitted unless it is a desk booking with nothing paid against it yet. */
  onChangePackage?: () => void;
  /** Omitted when the account cannot manage bookings, or it is already cancelled. */
  onCancel?: () => void;
  busy?: boolean;
}) {
  const { customer, payment, price } = booking;
  const pkg = price.package;
  const eligibility = booking.packageEligibility;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <Chip className={'border ' + bookingStatusTone(booking.status)}>
          {bookingStatusLabel(booking.status)}
        </Chip>
        <Chip className="font-mono">{booking.reference}</Chip>
        <Chip>{formatCents(payment.totalAmountMinor)}</Chip>
        {payment.balanceAmountMinor > 0 && booking.lifecycleStatus !== 'cancelled' && (
          <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
            {formatCents(payment.balanceAmountMinor)} owing
          </Chip>
        )}
        {eligibility?.status === 'pending' && (
          <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
            Student check due
          </Chip>
        )}
        {eligibility?.status === 'verified' && (
          <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
            Student verified
          </Chip>
        )}
      </div>

      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
        {/* Who to reach comes first: the desk opens a booking to call or email
            the person as often as to read what they paid. */}
        <Row label="Name">
          <span className="font-semibold">{customer.name}</span>
        </Row>
        <Row label="Email">
          <span className="break-all">{customer.email || '—'}</span>
        </Row>
        <Row label="Phone">
          <span className="font-mono">{customer.phone || '—'}</span>
        </Row>
        <Row label="Customer">
          <span className="font-mono">{customer.reference}</span>
        </Row>
        <Row label="Space">{bookingSpace(booking)}</Row>
        <Row label="When">{bookingWhen(booking)}</Row>
        <Row label="Priced as">
          {price.itemName} · {formatCents(price.unitAmountMinor)} per {price.unit}
          {price.quantity > 1 ? ` × ${price.quantity}` : ''}
        </Row>
        {pkg && (
          <>
            <Row label="Term">{durationLabel(pkg.duration)}</Row>
            <Row label="Access">{accessLabel(pkg.access)}</Row>
            {pkg.benefits.length > 0 && (
              <Row label="Includes">
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {pkg.benefits.map((b) => (
                    <li key={b.id}>
                      <span className="font-semibold">{b.title}</span>
                      {b.description ? `: ${b.description}` : ''}
                    </li>
                  ))}
                </ul>
              </Row>
            )}
          </>
        )}
        {eligibility?.status === 'pending' && (
          <Row label="Eligibility">
            <span className="text-warn-text">
              To be checked with the first payment
              {pkg?.eligibility.instructions ? `: ${pkg.eligibility.instructions}` : ''}
            </span>
          </Row>
        )}
        {eligibility?.status === 'verified' && (
          <Row label="Eligibility">
            {eligibility.evidenceNote}
            {eligibility.verifiedAt ? ` · ${stamp(eligibility.verifiedAt)}` : ''}
          </Row>
        )}
        <Row label="Paid">
          <span className="font-semibold tabular-nums">{formatCents(payment.paidAmountMinor)}</span>
        </Row>
        {payment.balanceAmountMinor > 0 && (
          <Row label="Balance">
            <span className="font-semibold text-warn-text tabular-nums">
              {formatCents(payment.balanceAmountMinor)}
            </span>
          </Row>
        )}
        <Row label="Booked">{stamp(booking.bookedAt)}</Row>
        {booking.internalNote && <Row label="Note">{booking.internalNote}</Row>}
        {booking.cancelledAt && (
          <Row label="Cancelled">
            {stamp(booking.cancelledAt)}
            {booking.cancellationReason ? ` · ${booking.cancellationReason}` : ''}
          </Row>
        )}
      </dl>

      {booking.payments.length > 0 && (
        <div className="flex flex-col gap-2">
          <h4 className="m-0 font-mono text-sm tracking-wide text-ink-muted uppercase">Payments</h4>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {booking.payments.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-xl border border-hairline bg-raised px-4 py-3 text-base"
              >
                <span className="font-mono text-md text-ink-muted">{p.reference}</span>
                <span>{p.methodLabel}</span>
                <span className="font-mono text-md text-ink-muted">{stamp(p.recordedAt)}</span>
                <span className="font-semibold tabular-nums">{formatCents(p.amountMinor)}</span>
                {p.note && <span className="w-full text-md text-ink-muted">{p.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(onCollect || onChangePackage || onCancel) && (
        <div className="flex flex-wrap gap-2">
          {onCollect && (
            <Button onClick={onCollect} disabled={busy}>
              Collect {formatCents(payment.balanceAmountMinor)}
            </Button>
          )}
          {onChangePackage && (
            <Button variant="quiet" onClick={onChangePackage} disabled={busy}>
              Change package
            </Button>
          )}
          {onCancel && (
            <Button variant="danger" onClick={onCancel} disabled={busy}>
              Cancel booking
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
